import React from 'react';
import { Button, Stack, Box } from '@mui/material';
import { useFormik } from 'formik';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { objectAttribute } from 'utils/resources/ObjectAttributes';
import { vetPortalPermissionOptions } from 'utils/resources/PracticeRoles';

import {
  Section,
  Checkbox,
  Select,
  Multiselect,
  TextField,
} from 'components/shared';
import { InfoCircle } from '../InfoCircle';

const StyledTooltip = styled.div`
  display: grid;
  grid-row-gap: 1rem;
`;

const StyledCheckboxContainer = styled.div`
  display: grid;
  grid-column-gap: 3rem;
  grid-row-gap: 1.5rem;
  grid-template-columns: repeat(2, 1fr);
  padding: 1rem 0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }

  &.retire-vet {
    margin-top: -0.75rem;
    padding-top: 0rem;

    &&&&&& .MuiTypography-root {
      font-family: 'Whitney Book';
    }
  }
`;

export const AddOrEditPersonForm = ({
  payToPractice = true,
  mode,
  onAdd,
  header,
  saveFormState,
  onEdit,
  validationErrors,
  initialData,
  onCancel,
  showVetPortalPermissions = false,
  disableEditingNameAndEmail = false,
  displayRetireButton = false,
  loggedInEmail,
}) => {
  const [rolesOptions, setRolesOptions] = React.useState([]);
  const [interestsOptions, setInterestsOptions] = React.useState([]);
  const [boardCertOptions, setBoardCertOptions] = React.useState([]);
  const [degreeOptions, setDegreeOptions] = React.useState([]);
  const [submissionInProgress, setSubmissionInProgress] = React.useState(false);

  const valid = Object.keys(validationErrors).length === 0;

  React.useEffect(() => {
    Promise.all([
      objectAttribute({ objectType: 'vet', attribute: 'relationship_types' }),
      objectAttribute({ objectType: 'vet', attribute: 'interests' }),
      objectAttribute({ objectType: 'vet', attribute: 'board_certifications' }),
      objectAttribute({ objectType: 'vet', attribute: 'vet_degrees' }),
    ]).then((responses) => {
      setRolesOptions(responses[0]);
      setInterestsOptions(responses[1]);
      setBoardCertOptions(responses[2]);
      setDegreeOptions(responses[3]);
    });
    const clearForm = onCancel;
    window.addEventListener('beforeunload', clearForm);

    return () => {
      window.removeEventListener('beforeunload', clearForm);
    };
  }, []);

  const formik = useFormik({
    initialValues: initialData,
    // Clicking the cancel button clears the data in editingPerson
    // If we then try to edit a person, we want initialData set to that person's data
    // The value of initialData correctly updates to reflect the person's data however
    // formik does not, by default, update the form values to reflect the new initial values
    // We can configure that behavior via enableReinitialize
    enableReinitialize: false,
  });

  const handleSubmit = (e) => {
    setSubmissionInProgress(true);
    e.preventDefault();
    // This 'submit' event will bubble up to the form rendered by the ClinicPersonnel component
    // That component doesn't preventDefault when its form is submitted, we do it here instead
    e.stopPropagation();

    // Form fields could potentially have values auto filled by the browser
    // When the values are auto-filled none of the fields will be touched and therefore
    // auto-filled fields with a validation error will not show an error message
    // formik's handleSubmit callback doesn't enable us to touch the fields before an error message is shown
    // so we imperatively touch all fields and then call formik.handleSubmit
    const values = formik.values;

    saveFormState(values).then(({ validationErrors }) => {
      const touched = Object.keys(values).reduce((touched, key) => {
        touched[key] = true;
        return touched;
      }, {});
      formik.setTouched(touched);

      if (Object.keys(validationErrors).length === 0) {
        mode === 'add'
          ? onAdd(values).then(() => setSubmissionInProgress(false))
          : onEdit(values).then(() => setSubmissionInProgress(false));
      } else {
        setSubmissionInProgress(false);
      }
    });
  };

  const persistFormState = React.useMemo(() => {
    // We need to maintain a reference to the debounced function
    // If we simply store it as a function (e.g. const persistFormState = debounce(saveFormState, 300))
    // a new function will be created on every render, and we'll never call a debounced function

    // One way to maintain the reference is by memoizing the function via React.useMemo
    // TODO: either find a hook in a library or extract this into a custom hook

    return debounce(saveFormState, 500);
  }, []);

  const handleChange = (e) => {
    const { name, checked, type } = e.target;
    const value = type === 'checkbox' ? checked : e.target.value;

    if (type === 'checkbox') {
      formik.setFieldTouched(name, true);
    }

    formik.handleChange(e);
    persistFormState({ ...formik.values, [name]: value });
  };

  const handleMultiSelectChange = (name, values) => {
    formik.setFieldValue(name, values);
    persistFormState({ ...formik.values, [name]: values });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    saveFormState({ ...formik.values, [name]: value }).then(() =>
      formik.handleBlur(e)
    );
  };

  const hasErrors = Object.keys(validationErrors).length > 0;

  const generateErrorMessage = (fieldName) => {
    const errorPresent =
      validationErrors && validationErrors.hasOwnProperty(fieldName);

    if (fieldName === 'email') {
      return (formik.touched['email'] ||
        formik.touched['mainContact'] ||
        formik.touched['vetPortalAdmin']) &&
        errorPresent
        ? validationErrors[fieldName].join(' ')
        : '';
    }

    return formik.touched[fieldName] && errorPresent
      ? validationErrors[fieldName].join(' ')
      : '';
  };

  const isVet =
    formik.values.practiceRoles &&
    formik.values.practiceRoles.includes('veterinarian');
  const isOwner =
    formik.values.practiceRoles &&
    formik.values.practiceRoles.includes('owner');

  const editNameAndEmailHelperText =
    initialData.email === loggedInEmail ? (
      <div>
        Please go to{' '}
        <a
          target="_blank"
          href="https://www.platinumperformance.com/on/demandware.store/Sites-platinumperformance-Site/en_US/Account-Show"
        >
          Account Overview
        </a>{' '}
        to edit this info.
      </div>
    ) : (
      <div>
        Please have this person login to update this info or call us at{' '}
        <a href="tel:866-553-2400">866-553-2400</a>.
      </div>
    );

  const standardFields = [
    <TextField
      name="firstName"
      required
      disabled={disableEditingNameAndEmail}
      label="First Name"
      onChange={handleChange}
      onBlur={handleBlur}
      value={formik.values.firstName}
      errorMessage={generateErrorMessage('firstName')}
      error={!!generateErrorMessage('firstName')}
    />,
    <TextField
      name="lastName"
      required
      disabled={disableEditingNameAndEmail}
      label="Last Name"
      onChange={handleChange}
      onBlur={handleBlur}
      value={formik.values.lastName}
      errorMessage={generateErrorMessage('lastName')}
      error={!!generateErrorMessage('lastName')}
    />,
    <TextField
      name="email"
      required={
        formik.values.mainContact ||
        formik.values.vetPortalAdmin ||
        (isVet && !payToPractice) ||
        disableEditingNameAndEmail
      }
      disabled={disableEditingNameAndEmail}
      label="Email Address"
      onChange={handleChange}
      onBlur={handleBlur}
      value={formik.values.email}
      errorMessage={generateErrorMessage('email')}
      error={!!generateErrorMessage('email')}
      helperText={disableEditingNameAndEmail && editNameAndEmailHelperText}
    />,
    <TextField
      label="Phone Number"
      id="phone"
      phoneNumberMask
      variant="outlined"
      name="phone"
      onChange={handleChange}
      onBlur={handleBlur}
      value={formik.values.phone}
      errorMessage={generateErrorMessage('phone')}
      error={!!generateErrorMessage('phone')}
    />,
    <Multiselect
      name="practiceRoles"
      value={formik.values.practiceRoles}
      label="Practice Role(s)"
      required
      errorMessage={
        // Checking formik.values.practiceRoles is a workaround
        // generateErrorMessage is being executed too early and the error
        // message is rendering before the component is updated w/ the results
        // of our debounced update. practiceRoles simply has a presence validation
        // so we can just read its value from formik.values.practiceRoles as that
        // corresponds to whether or not the value is present to the user
        formik.values.practiceRoles.length === 0 &&
        generateErrorMessage('practiceRoles')
      }
      error={
        // Checking formik.values.practiceRoles is a workaround
        // generateErrorMessage is being executed too early and the error
        // message is rendering before the component is updated w/ the results
        // of our debounced update. practiceRoles simply has a presence validation
        // so we can just read its value from formik.values.practiceRoles as that
        // corresponds to whether or not the value is present to the user
        formik.values.practiceRoles.length === 0 &&
        generateErrorMessage('practiceRoles')
      }
      options={rolesOptions}
      onChange={handleMultiSelectChange}
      onBlur={() => formik.setFieldTouched('practiceRoles', true)}
      helperText='You may select multiple roles, e.g. "Owner," here.'
    />,
  ];
  const vetFieldsWithoutRetireOption = [
    <TextField
      name="vetLicenseNumber"
      variant="outlined"
      label="Veterinary License #"
      value={formik.values.vetLicenseNumber}
      errorMessage={generateErrorMessage('vetLicenseNumber')}
      error={!!generateErrorMessage('vetLicenseNumber')}
      onChange={handleChange}
      onBlur={handleBlur}
    />,
  ];
  const vetFieldsWithRetireOption = [
    <TextField
      name="vetLicenseNumber"
      variant="outlined"
      label="Veterinary License #"
      value={formik.values.vetLicenseNumber}
      errorMessage={generateErrorMessage('vetLicenseNumber')}
      error={!!generateErrorMessage('vetLicenseNumber')}
      onChange={handleChange}
      onBlur={handleBlur}
    />,
    <StyledCheckboxContainer
      className="retire-vet"
      sectionComponentSize="large"
    >
      <Checkbox
        name="retireVet"
        value={formik.values.retireVet}
        onChange={handleChange}
        onBlur={handleBlur}
        label="This veterinarian is retired."
        infoText={
          <div>
            A retired veterinarian will be removed from your practice.
            <br />
            If you have retired a veterinarian in error,
            <br />
            please call a Platinum Advisor at 866-553-2400 for assistance
            <br />
            as this action cannot be undone in the Veterinary Portal.
          </div>
        }
      />
    </StyledCheckboxContainer>,
  ];
  const extraVetFields = [
    <Multiselect
      name="areaOfInterest"
      value={formik.values.areaOfInterest}
      label="Area of Interest"
      options={interestsOptions}
      onChange={handleMultiSelectChange}
      onBlur={handleBlur}
    />,
    <Multiselect
      name="boardCertifications"
      value={formik.values.boardCertifications}
      label="Board Certifications"
      options={boardCertOptions}
      onChange={handleMultiSelectChange}
      onBlur={handleBlur}
    />,
    <TextField
      name="vetSchool"
      variant="outlined"
      label="Veterinary School"
      value={formik.values.vetSchool}
      onChange={handleChange}
      onBlur={handleBlur}
    />,
    <Select
      name="vetDegree"
      value={formik.values.vetDegree}
      onChange={handleChange}
      onBlur={handleBlur}
      label="Veterinary Degree"
      options={degreeOptions}
      needsDefaultOption
    />,
  ];
  const mainContactCheckbox = (
    <StyledCheckboxContainer sectionComponentSize="large">
      <Checkbox
        required
        errorMessage={generateErrorMessage('mainContact')}
        error={!!generateErrorMessage('mainContact')}
        name="mainContact"
        value={formik.values.mainContact}
        onChange={handleChange}
        onBlur={handleBlur}
        label="Main Business Contact for Practice"
      />
    </StyledCheckboxContainer>
  );
  const ownerFields = [
    <TextField
      name="ownerPercentage"
      value={formik.values.ownerPercentage}
      onChange={handleChange}
      onBlur={handleBlur}
      label="Ownership Percentage"
      type="number"
    />,
  ];
  const vetPortalPermissionFields = [
    <Select
      required
      name="vetPortalViewingPermission"
      value={formik.values.vetPortalViewingPermission}
      onChange={handleChange}
      onBlur={handleBlur}
      label={
        <div>
          Vet Portal Viewing Permissions
          <InfoCircle
            clickable
            text={
              <StyledTooltip>
                <div>
                  <strong>None: </strong>No access to the practice-specific
                  pages
                  <br />
                  (e.g. Dashboards, Billing) of the Veterinary Portal.
                </div>
                {payToPractice && (
                  <>
                    <div>
                      <strong>Account/Practice Owner: </strong>Access to view
                      the practice's
                      <br />
                      records (including all vet records).
                    </div>
                    <div>
                      <strong>Veterinarian: </strong>Access to view the Billing
                      page,
                      <br />
                      non-financial practice statistics, and non-financial
                      <br />
                      Direct Dispense records for just this vet.
                    </div>
                  </>
                )}

                {!payToPractice && (
                  <>
                    <div>
                      <strong>Account/Practice Owner: </strong>Access to view
                      the practice's records.
                    </div>
                    <div>
                      <strong>Veterinarian: </strong>Access to view the Billing
                      page and just
                      <br />
                      this vet's own financial records.
                    </div>
                  </>
                )}
              </StyledTooltip>
            }
          />
        </div>
      }
      options={vetPortalPermissionOptions}
      needsDefaultOption
    />,
    <StyledCheckboxContainer sectionComponentSize="large">
      <Checkbox
        required
        errorMessage={generateErrorMessage('mainContact')}
        error={!!generateErrorMessage('mainContact')}
        name="mainContact"
        value={formik.values.mainContact}
        onChange={handleChange}
        onBlur={handleBlur}
        label="Main Business Contact for Practice"
      />
      <Checkbox
        name="vetPortalAdmin"
        value={formik.values.vetPortalAdmin}
        onChange={handleChange}
        onBlur={handleBlur}
        label="Vet Portal Admin - Can Change Permissions"
      />
    </StyledCheckboxContainer>,
  ];

  const loadingPanelWithButton = [
    <Button
      type="button"
      disabled
      className="ecom-button spinner outlined small primary next"
    >
      <FontAwesomeIcon icon={faCircleNotch} size="lg" color="#0F6C9D" spin />
    </Button>,
    <Button
      onClick={onCancel}
      sectionComponentSize="medium"
      fullWidth
      color="primary"
      variant="outlined"
    >
      Cancel
    </Button>,
  ];

  const saveButtonText =
    mode === 'add'
      ? 'Add'
      : formik.values.retireVet
      ? 'Retire Vet & Remove From Practice'
      : 'Save';
  const buttons = [
    <Button
      type="submit"
      disabled={hasErrors}
      sectionComponentSize="medium"
      fullWidth
      color="primary"
      variant="contained"
    >
      {saveButtonText}
    </Button>,
    <Button
      onClick={onCancel}
      sectionComponentSize="medium"
      fullWidth
      color="primary"
      variant="outlined"
    >
      Cancel
    </Button>,
  ];

  const components = standardFields
    .concat(
      isVet
        ? displayRetireButton
          ? vetFieldsWithRetireOption
          : vetFieldsWithoutRetireOption
        : []
    )
    .concat(isVet ? extraVetFields : [])
    .concat(isOwner ? ownerFields : [])
    .concat(showVetPortalPermissions ? vetPortalPermissionFields : [])
    .concat(showVetPortalPermissions ? [] : mainContactCheckbox) // main contact checkbox is grouped in vetPortalPermissionFields
    .concat(submissionInProgress ? loadingPanelWithButton : buttons);

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Section components={components} />
    </form>
  );
};
AddOrEditPersonForm.defaultProps = {
  initialData: {
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    practiceRoles: [],
    vetLicenseNumber: '',
    areaOfInterest: [],
    ownerPercentage: '',
    boardCertifications: [],
    vetSchool: '',
    vetDegree: '',
    mainContact: false,
  },
};
